import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Compass, Newspaper, TrendingUp, User, LogOut, Coins, Flame, Trophy, Music, Users, Calendar, Gift, Search, Sun, Moon, Crown, Ticket, Heart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { NotificationBell } from "@/components/NotificationBell";
import { MessageBadge } from "@/components/MessageBadge";
import { OnboardingOverlay } from "@/components/OnboardingOverlay";
import { NotificationManager } from "@/components/NotificationManager";
import { InstallPrompt } from "@/components/InstallPrompt";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { UpdateAvailable } from "@/components/UpdateAvailable";
import { PushDiscoveryBanner } from "@/components/PushDiscoveryBanner";
import { WelcomeCelebration } from "@/components/WelcomeCelebration";
import { AnimatePresence } from "framer-motion";

export const Layout = ({ children }) => {
  const { user, logout, streak } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (user && !localStorage.getItem(`onboarding_done_${user.id}`)) {
      setShowOnboarding(true);
    }
  }, [user]);

  const handleOnboardingComplete = () => {
    if (user) localStorage.setItem(`onboarding_done_${user.id}`, "true");
    setShowOnboarding(false);
  };

  const isArtist = user?.role === "artist";

  const navItems = isArtist
    ? [
        { to: "/dashboard", icon: Music, label: "Espace" },
        { to: "/discover", icon: Compass, label: "Explorer" },
        { to: "/leaderboard", icon: Crown, label: "Classement" },
        { to: "/rewards", icon: Gift, label: "Encheres" },
        { to: "/challenges", icon: Trophy, label: "Defis" },
        { to: "/profile", icon: User, label: "Profil" },
      ]
    : [
        { to: "/discover", icon: Compass, label: "Explorer" },
        { to: "/feed", icon: Newspaper, label: "Feed" },
        { to: "/leaderboard", icon: Crown, label: "Classement" },
        { to: "/rewards", icon: Gift, label: "Encheres" },
        { to: "/challenges", icon: Trophy, label: "Defis" },
        { to: "/profile", icon: User, label: "Profil" },
      ];

  const streakCount = streak?.streak_count || 0;

  return (
    <div className="min-h-screen bg-background relative" data-testid="app-layout">
      {/* Onboarding */}
      <AnimatePresence>
        {showOnboarding && <OnboardingOverlay onComplete={handleOnboardingComplete} role={user?.role || "fan"} />}
      </AnimatePresence>

      {/* Push Notification Manager */}
      <NotificationManager />

      {/* PWA UX */}
      <OfflineIndicator />
      <InstallPrompt />
      <UpdateAvailable />
      <PushDiscoveryBanner />
      <WelcomeCelebration />

      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-40 glass glass-border">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 max-w-5xl mx-auto">
          <NavLink to="/discover" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <span className="font-heading font-bold text-lg sm:text-2xl text-gradient">ReallyFan</span>
          </NavLink>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Streak */}
            <div
              className={`flex items-center gap-1 px-2 py-1 rounded-full ${
                streakCount > 0
                  ? "bg-orange-500/10 border border-orange-500/20"
                  : "bg-foreground/5 border border-foreground/5"
              }`}
              data-testid="streak-display"
              title={`Streak: ${streakCount} jour${streakCount > 1 ? "s" : ""}`}
            >
              <Flame
                className={`w-3.5 h-3.5 ${
                  streakCount >= 7
                    ? "text-orange-400 drop-shadow-[0_0_6px_rgba(255,165,0,0.7)]"
                    : streakCount > 0
                    ? "text-orange-400"
                    : "text-muted-foreground"
                }`}
                fill={streakCount > 0 ? "currentColor" : "none"}
                strokeWidth={streakCount > 0 ? 0 : 1.5}
              />
              <span className={`font-mono-credits text-xs font-bold ${streakCount > 0 ? "text-orange-400" : "text-muted-foreground"}`}>
                {streakCount}
              </span>
            </div>

            {/* Search */}
            <NavLink to="/search" className="flex items-center justify-center w-7 h-7 rounded-full bg-foreground/5 glass-border text-muted-foreground hover:text-[#FF6B35] transition-colors" data-testid="search-btn">
              <Search className="w-3.5 h-3.5" />
            </NavLink>

            {/* Credits */}
            <NavLink to="/credits" className="flex items-center gap-1 bg-foreground/5 rounded-full px-2 py-1 glass-border hover:bg-[#FF6B35]/10 transition-colors" data-testid="credits-display" title="Recharger mes credits">
              <Coins className="w-3.5 h-3.5 text-[#FF6B35]" />
              <span className="font-mono-credits text-xs font-medium text-foreground">{user?.credits_balance ?? 0}</span>
              <span className="text-[9px] text-[#FF6B35] font-bold">+</span>
            </NavLink>

            {/* Messages */}
            <MessageBadge />

            {/* Notifications */}
            <NotificationBell />

            {/* Theme toggle */}
            <button onClick={toggleTheme} className="flex items-center justify-center w-7 h-7 rounded-full bg-foreground/5 glass-border text-muted-foreground hover:text-[#FF6B35] transition-colors" data-testid="theme-toggle" title={theme === "dark" ? "Mode clair" : "Mode sombre"}>
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        {/* Second row - always visible */}
        <div className="flex items-center justify-around px-3 pb-2 border-t border-foreground/5 pt-1.5 max-w-5xl mx-auto">
          <NavLink to="/billetterie" className={`flex flex-col items-center gap-0.5 px-2 py-0.5 rounded-xl transition-colors ${location.pathname.startsWith("/billetterie") ? "text-[#FF6B35]" : "text-foreground/50 hover:text-foreground/70"}`} data-testid="billetterie-link">
            <Ticket className="w-4 h-4" />
            <span className="text-[8px] font-medium">Billets</span>
          </NavLink>
          <NavLink to="/playlists" className={`flex flex-col items-center gap-0.5 px-2 py-0.5 rounded-xl transition-colors ${location.pathname.startsWith("/playlists") ? "text-[#FF6B35]" : "text-foreground/50 hover:text-foreground/70"}`} data-testid="playlists-link">
            <Music className="w-4 h-4" />
            <span className="text-[8px] font-medium">Playlists</span>
          </NavLink>
          <NavLink to="/friends" className={`flex flex-col items-center gap-0.5 px-2 py-0.5 rounded-xl transition-colors ${location.pathname === "/friends" ? "text-pink-500" : "text-foreground/50 hover:text-foreground/70"}`} data-testid="friends-link">
            <Heart className="w-4 h-4" />
            <span className="text-[8px] font-medium">Amis</span>
          </NavLink>
          <button onClick={logout} className="flex flex-col items-center gap-0.5 px-2 py-0.5 rounded-xl text-foreground/50 hover:text-red-400 transition-colors" data-testid="logout-button">
            <LogOut className="w-4 h-4" />
            <span className="text-[8px] font-medium">Quitter</span>
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="pt-28 pb-24 sm:pb-28 px-3 sm:px-4 max-w-5xl mx-auto">
        {children}
      </main>

      {/* Bottom navigation - truly fixed with safe area */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 nav-bg border-t border-border shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="bottom-nav"
      >
        <div className="flex items-center justify-around h-[64px] sm:h-[72px] px-1 sm:px-2">
          {navItems.map(({ to, icon: Icon, label }) => {
            const isActive = location.pathname === to || location.pathname.startsWith(to + "/");
            return (
              <NavLink
                key={to}
                to={to}
                className={`flex flex-col items-center gap-0.5 px-1.5 sm:px-2 py-1 rounded-2xl transition-colors duration-200 ${
                  isActive
                    ? "text-[#FF6B35]"
                    : "text-foreground/60 hover:text-foreground"
                }`}
                data-testid={`nav-${label.toLowerCase()}`}
              >
                <Icon className="w-5 h-5 sm:w-7 sm:h-7" strokeWidth={isActive ? 2.5 : 1.8} />
                <span className={`text-[9px] sm:text-[11px] font-semibold leading-tight ${isActive ? "text-[#FF6B35]" : "text-foreground/70"}`}>{label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
};
